import { useState, useCallback, useEffect, useRef } from "react";

import {
    BATCH_CANDY_DISPENSER_COMPONENT_ADDRESS,
    BATCH_DCKSLAP_PER_BATCH,
    BATCH_REDDICKS_FEE_AMOUNT,
    BATCH_XRD_FEE_AMOUNT,
    DCKSLAP_RESOURCE_ADDRESS,
    REDDICKS_RESSOURCE_ADDRESS,
    RADIX_TOKEN_RESOURCE_ADDRESS,
    RADIX_API_GATEWAY_URL,
    CANDY_MACHINE_BOT_BADGE_ADDRESS,
    CANDY_MACHINE_CANDIES_RESOURCE_ADDRESS,
} from "../../constants";
import { useRadixConnect } from "../../radix_connect";
import {
    extractBatchRewards,
    validateBatchConfig,
} from "./batchCandyMachineUtils";

const BATCH_SESSION_KEYS = {
    PLAYS_COUNT: "batch_candy_plays_count",
    CONFIRMED_COUNT: "batch_candy_confirmed_count",
    RECENT_CONFIRMATIONS: "batch_candy_recent_confirmations",
};

const GAME_STATUS = {
    IDLE: "IDLE",
    PROCESSING_TX: "PROCESSING_TX",
    VERIFYING_EVENT: "VERIFYING_EVENT",
    CONFIRMED: "CONFIRMED",
    TIMEOUT: "TIMEOUT",
};

const EVENT_POLL_INTERVAL_MS = 4000;
const EVENT_POLL_MAX_RETRIES = 40;
const XRD_GAS_BUFFER = 0.8;

const loadBatchSessionStats = () => {
    try {
        return {
            playsCount: Number.parseInt(
                localStorage.getItem(BATCH_SESSION_KEYS.PLAYS_COUNT) || "0",
                10
            ),
            confirmedCount: Number.parseInt(
                localStorage.getItem(BATCH_SESSION_KEYS.CONFIRMED_COUNT) || "0",
                10
            ),
            recentConfirmations: JSON.parse(
                localStorage.getItem(BATCH_SESSION_KEYS.RECENT_CONFIRMATIONS) || "[]"
            ),
        };
    } catch {
        return {
            playsCount: 0,
            confirmedCount: 0,
            recentConfirmations: [],
        };
    }
};

const saveBatchSessionStats = (stats) => {
    try {
        localStorage.setItem(
            BATCH_SESSION_KEYS.PLAYS_COUNT,
            String(stats.playsCount || 0)
        );
        localStorage.setItem(
            BATCH_SESSION_KEYS.CONFIRMED_COUNT,
            String(stats.confirmedCount || 0)
        );
        localStorage.setItem(
            BATCH_SESSION_KEYS.RECENT_CONFIRMATIONS,
            JSON.stringify(stats.recentConfirmations || [])
        );
    } catch (error) {
        console.warn("Failed to persist batch stats:", error);
    }
};

const getWalletErrorMessage = (error) => {
    if (!error) {
        return "Transaction failed.";
    }

    const rawMessage =
        typeof error === "string" ? error : error.message || "Transaction failed.";
    const normalized = rawMessage.toLowerCase();

    if (normalized.includes("rejected") || normalized.includes("cancel")) {
        return "Transaction was cancelled in wallet.";
    }

    if (normalized.includes("insufficient")) {
        return "Wallet reported insufficient funds for this transaction.";
    }

    return rawMessage;
};

const getRequiredAmounts = () => ({
    dckslap: Number(BATCH_DCKSLAP_PER_BATCH),
    reddicks: Number.parseFloat(BATCH_REDDICKS_FEE_AMOUNT),
    xrd: Number.parseFloat(BATCH_XRD_FEE_AMOUNT),
    xrdWithBuffer: Number.parseFloat(BATCH_XRD_FEE_AMOUNT) + XRD_GAS_BUFFER,
});

const hasValidRequiredAmounts = (required) => {
    return (
        Number.isFinite(required.dckslap) &&
        required.dckslap > 0 &&
        Number.isFinite(required.reddicks) &&
        required.reddicks >= 0 &&
        Number.isFinite(required.xrd) &&
        required.xrd >= 0 &&
        Number.isFinite(required.xrdWithBuffer)
    );
};

export const useBatchCandyMachine = () => {
    const { rdt, selectedAccount } = useRadixConnect();

    const [gameStatus, setGameStatus] = useState(GAME_STATUS.IDLE);
    const [error, setError] = useState(null);
    const [sessionStats, setSessionStats] = useState(loadBatchSessionStats);
    const [lastBatchReceipt, setLastBatchReceipt] = useState(null);

    const pollingAbortRef = useRef(null);

    const isProcessing =
        gameStatus === GAME_STATUS.PROCESSING_TX ||
        gameStatus === GAME_STATUS.VERIFYING_EVENT;

    const appendRecentConfirmation = useCallback((confirmation) => {
        setSessionStats((previous) => {
            const recentConfirmations = [
                confirmation,
                ...(previous.recentConfirmations || []),
            ].slice(0, 20);

            const next = { ...previous, recentConfirmations };
            saveBatchSessionStats(next);
            return next;
        });
    }, []);

    const incrementSessionCounter = useCallback((key) => {
        setSessionStats((previous) => {
            const next = {
                ...previous,
                [key]: Number(previous[key] || 0) + 1,
            };
            saveBatchSessionStats(next);
            return next;
        });
    }, []);

    const clearActivePolling = useCallback(() => {
        if (pollingAbortRef.current) {
            pollingAbortRef.current.abort();
            pollingAbortRef.current = null;
        }
    }, []);

    useEffect(() => clearActivePolling, [clearActivePolling]);

    const checkBatchBalances = useCallback(async () => {
        const required = getRequiredAmounts();

        if (!selectedAccount?.address) {
            return {
                hasEnough: false,
                missing: "ALL",
                balances: { dckslap: 0, reddicks: 0, xrd: 0 },
                required,
            };
        }

        try {
            let dckslapBalance = 0;
            let reddicksBalance = 0;
            let xrdBalance = 0;
            let cursor = null;
            let stateVersion = null;
            let foundDckslap = false;
            let foundReddicks = false;
            let foundXrd = false;

            do {
                const requestBody = { address: selectedAccount.address };
                if (cursor) {
                    requestBody.cursor = cursor;
                    requestBody.at_ledger_state = { state_version: stateVersion };
                }

                const response = await fetch(
                    `${RADIX_API_GATEWAY_URL}/state/entity/page/fungibles/`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(requestBody),
                    }
                );

                if (!response.ok) {
                    return {
                        hasEnough: false,
                        missing: "ALL",
                        balances: { dckslap: 0, reddicks: 0, xrd: 0 },
                        required,
                    };
                }

                const data = await response.json();
                const items = data.items || [];

                for (const item of items) {
                    if (item.resource_address === DCKSLAP_RESOURCE_ADDRESS) {
                        dckslapBalance = Number.parseFloat(item.amount) || 0;
                        foundDckslap = true;
                    } else if (item.resource_address === REDDICKS_RESSOURCE_ADDRESS) {
                        reddicksBalance = Number.parseFloat(item.amount) || 0;
                        foundReddicks = true;
                    } else if (item.resource_address === RADIX_TOKEN_RESOURCE_ADDRESS) {
                        xrdBalance = Number.parseFloat(item.amount) || 0;
                        foundXrd = true;
                    }

                    if (foundDckslap && foundReddicks && foundXrd) {
                        break;
                    }
                }

                cursor = data.next_cursor || null;
                stateVersion = data.ledger_state?.state_version || null;
            } while (cursor && !(foundDckslap && foundReddicks && foundXrd));

            const balances = {
                dckslap: dckslapBalance,
                reddicks: reddicksBalance,
                xrd: xrdBalance,
            };

            const hasDckslap = dckslapBalance >= required.dckslap;
            const hasReddicks = reddicksBalance >= required.reddicks;
            const hasXrd = xrdBalance >= required.xrdWithBuffer;

            if (hasDckslap && hasReddicks && hasXrd) {
                return {
                    hasEnough: true,
                    missing: null,
                    balances,
                    required,
                };
            }

            const missingTokens = [];
            if (!hasDckslap) {
                missingTokens.push("DCKSLAP");
            }
            if (!hasReddicks) {
                missingTokens.push("REDDICKS");
            }
            if (!hasXrd) {
                missingTokens.push("XRD");
            }

            return {
                hasEnough: false,
                missing: missingTokens.join("_"),
                balances,
                required,
            };
        } catch (networkError) {
            console.error("Batch balance check failed:", networkError);
            return {
                hasEnough: false,
                missing: "ALL",
                balances: { dckslap: 0, reddicks: 0, xrd: 0 },
                required,
            };
        }
    }, [selectedAccount]);

    const waitForBatchDepositEvent = useCallback(
        async ({ accountAddress, choice, startTimestamp }) => {
            clearActivePolling();

            const abortController = new AbortController();
            pollingAbortRef.current = abortController;

            const expectedCount = Number(BATCH_DCKSLAP_PER_BATCH);
            const seenTxHashes = new Set();
            const allRewards = [];
            let matchedCount = 0;
            let lastGatewayError = null;

            for (let retry = 0; retry < EVENT_POLL_MAX_RETRIES; retry += 1) {
                try {
                    const response = await fetch(
                        `${RADIX_API_GATEWAY_URL}/stream/transactions`,
                        {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            signal: abortController.signal,
                            body: JSON.stringify({
                                limit_per_page: 100,
                                from_ledger_state: { timestamp: startTimestamp },
                                opt_ins: {
                                    receipt_output: false,
                                    balance_changes: true,
                                    manifest_instructions: true,
                                },
                                manifest_badges_presented_filter: [
                                    CANDY_MACHINE_BOT_BADGE_ADDRESS,
                                ],
                                transaction_status_filter: "Success",
                            }),
                        }
                    );

                    if (!response.ok) {
                        throw new Error(
                            "Failed to query transactions for event verification."
                        );
                    }

                    const data = await response.json();
                    const transactions = data.items || [];

                    for (const tx of transactions) {
                        const txHash = tx.intent_hash;
                        if (!txHash || seenTxHashes.has(txHash)) {
                            continue;
                        }

                        const manifestInstructions = tx.manifest_instructions;
                        if (typeof manifestInstructions !== "string") {
                            continue;
                        }

                        const sendCandiesIndex = manifestInstructions.lastIndexOf("send_candies");
                        if (sendCandiesIndex === -1) {
                            continue;
                        }

                        const instructionsAfterMethod = manifestInstructions.substring(sendCandiesIndex);

                        if (
                            instructionsAfterMethod.includes(accountAddress) &&
                            instructionsAfterMethod.includes(`${choice}u16`)
                        ) {
                            seenTxHashes.add(txHash);
                            matchedCount += 1;

                            const rewards = extractBatchRewards(
                                tx,
                                accountAddress,
                                CANDY_MACHINE_CANDIES_RESOURCE_ADDRESS
                            );
                            allRewards.push(...rewards);
                        }
                    }

                    if (matchedCount >= expectedCount) {
                        return {
                            status: "matched",
                            rewards: allRewards,
                            totalTransactions: matchedCount,
                            error: null,
                        };
                    }
                } catch (error) {
                    if (error?.name === "AbortError") {
                        throw error;
                    }
                    lastGatewayError = error;
                }

                await new Promise((resolve) =>
                    setTimeout(resolve, EVENT_POLL_INTERVAL_MS)
                );
            }

            if (matchedCount > 0) {
                return {
                    status: "matched",
                    rewards: allRewards,
                    totalTransactions: matchedCount,
                    error: null,
                };
            }

            return {
                status: "timeout",
                rewards: [],
                totalTransactions: 0,
                error: lastGatewayError,
            };
        },
        [clearActivePolling]
    );

    const playBatchRound = useCallback(
        async (choice) => {
            if (isProcessing) {
                return { success: false, reason: "already-processing" };
            }

            const accountAddress = selectedAccount?.address;

            if (!accountAddress) {
                setError("No account connected.");
                return { success: false, reason: "missing-account" };
            }

            const invalidConfigMessage = validateBatchConfig({
                componentAddress: BATCH_CANDY_DISPENSER_COMPONENT_ADDRESS,
                dckslapAddress: DCKSLAP_RESOURCE_ADDRESS,
                reddicksAddress: REDDICKS_RESSOURCE_ADDRESS,
                xrdAddress: RADIX_TOKEN_RESOURCE_ADDRESS,
                accountAddress,
            });

            if (invalidConfigMessage) {
                setError(invalidConfigMessage);
                return { success: false, reason: "invalid-config" };
            }

            if (choice < 1 || choice > 65535) {
                setError("Choice must be between 1 and 65535.");
                return { success: false, reason: "invalid-choice" };
            }

            setError(null);
            setLastBatchReceipt(null);
            setGameStatus(GAME_STATUS.PROCESSING_TX);

            const required = getRequiredAmounts();
            if (!hasValidRequiredAmounts(required)) {
                setError("Batch settings are invalid. Please contact support.");
                setGameStatus(GAME_STATUS.IDLE);
                return { success: false, reason: "invalid-required-amounts" };
            }
            const verificationStart = new Date(Date.now() - 5000).toISOString();

            const manifest = `
          CALL_METHOD
            Address("${accountAddress}")
            "withdraw"
            Address("${DCKSLAP_RESOURCE_ADDRESS}")
            Decimal("${required.dckslap}")
          ;
          TAKE_ALL_FROM_WORKTOP
            Address("${DCKSLAP_RESOURCE_ADDRESS}")
            Bucket("dckslap")
          ;
          CALL_METHOD
            Address("${accountAddress}")
            "withdraw"
            Address("${REDDICKS_RESSOURCE_ADDRESS}")
            Decimal("${required.reddicks}")
          ;
          TAKE_ALL_FROM_WORKTOP
            Address("${REDDICKS_RESSOURCE_ADDRESS}")
            Bucket("reddicks_fee")
          ;
          CALL_METHOD
            Address("${accountAddress}")
            "withdraw"
            Address("${RADIX_TOKEN_RESOURCE_ADDRESS}")
            Decimal("${required.xrd}")
          ;
          TAKE_ALL_FROM_WORKTOP
            Address("${RADIX_TOKEN_RESOURCE_ADDRESS}")
            Bucket("xrd_fee")
          ;
          CALL_METHOD
            Address("${BATCH_CANDY_DISPENSER_COMPONENT_ADDRESS}")
            "batch_deposit_dckslap"
            Bucket("dckslap")
            Bucket("reddicks_fee")
            Bucket("xrd_fee")
            Address("${accountAddress}")
            ${choice}u16
          ;
          CALL_METHOD
            Address("${accountAddress}")
            "deposit_batch"
            Expression("ENTIRE_WORKTOP")
          ;
      `;

            const walletResult = await rdt.walletApi.sendTransaction({
                transactionManifest: manifest,
                version: 1,
            });

            if (walletResult.isErr()) {
                setError(getWalletErrorMessage(walletResult.error));
                setGameStatus(GAME_STATUS.IDLE);
                return { success: false, reason: "wallet-error" };
            }

            const txIntentHash = walletResult.value?.transactionIntentHash || null;

            if (!txIntentHash) {
                setError("Transaction submitted, but no intent hash was returned.");
                setGameStatus(GAME_STATUS.IDLE);
                return { success: false, reason: "missing-intent-hash" };
            }

            incrementSessionCounter("playsCount");

            setGameStatus(GAME_STATUS.VERIFYING_EVENT);

            try {
                const verification = await waitForBatchDepositEvent({
                    accountAddress,
                    choice,
                    startTimestamp: verificationStart,
                });

                if (verification.status === "matched" && verification.totalTransactions > 0) {
                    const receipt = {
                        transactionIntentHash: txIntentHash,
                        totalTransactions: verification.totalTransactions,
                        confirmedAt: new Date().toISOString(),
                        account: accountAddress,
                        choice: Number(choice),
                        depositedAmount: Number(BATCH_DCKSLAP_PER_BATCH),
                        rewards: verification.rewards,
                    };

                    setLastBatchReceipt(receipt);
                    setGameStatus(GAME_STATUS.CONFIRMED);
                    incrementSessionCounter("confirmedCount");
                    appendRecentConfirmation(receipt);
                    clearActivePolling();

                    return { success: true, receipt };
                }

                setError(
                    verification.error?.message
                        ? `Event verification timed out after gateway errors: ${verification.error.message}`
                        : "Event verification timed out. Transaction may still have succeeded. Please verify in wallet/explorer."
                );
                setGameStatus(GAME_STATUS.TIMEOUT);
                clearActivePolling();
                return { success: false, reason: "verification-timeout" };
            } catch (pollingError) {
                if (pollingError?.name === "AbortError") {
                    return { success: false, reason: "aborted" };
                }

                setError(
                    pollingError?.message ||
                    "Failed to verify deposit event from gateway."
                );
                setGameStatus(GAME_STATUS.TIMEOUT);
                clearActivePolling();

                return { success: false, reason: "verification-error" };
            }
        },
        [
            appendRecentConfirmation,
            clearActivePolling,
            isProcessing,
            rdt,
            selectedAccount,
            incrementSessionCounter,
            waitForBatchDepositEvent,
        ]
    );

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    const clearReceipt = useCallback(() => {
        setLastBatchReceipt(null);
        setError(null);
        setGameStatus(GAME_STATUS.IDLE);
    }, []);

    const resetGameStatus = useCallback(() => {
        clearActivePolling();
        setGameStatus(GAME_STATUS.IDLE);
    }, [clearActivePolling]);

    const clearSessionStats = useCallback(() => {
        const empty = { playsCount: 0, confirmedCount: 0, recentConfirmations: [] };
        setSessionStats(empty);
        saveBatchSessionStats(empty);
    }, []);

    return {
        isProcessing,
        error,
        gameStatus,
        sessionStats,
        lastBatchReceipt,
        batchSize: BATCH_DCKSLAP_PER_BATCH,
        reddicksFee: BATCH_REDDICKS_FEE_AMOUNT,
        xrdFee: BATCH_XRD_FEE_AMOUNT,
        batchComponentAddress: BATCH_CANDY_DISPENSER_COMPONENT_ADDRESS,
        GAME_STATUS,
        playBatchRound,
        checkBatchBalances,
        clearError,
        clearReceipt,
        resetGameStatus,
        clearSessionStats,
    };
};

export default useBatchCandyMachine;
